/**
 * Utility functions for handling alert messages from backend responses
 */

/**
 * Extract message from API response (success or error)
 * @param {Object} response - API response object
 * @param {string} fallback - Fallback message if no message found
 * @returns {string} - Extracted message
 */
export function getResponseMessage(response, fallback = 'تم تنفيذ العملية') {
  // For success responses
  if (response && response.data) {
    return response.data.message || 
           response.data.msg || 
           response.data.success || 
           fallback;
  }
  
  // For direct data responses
  if (response && response.message) {
    return response.message;
  }
  
  return fallback;
}

/**
 * Extract error message from API error response
 * @param {Object} error - Error object from API
 * @param {string} fallback - Fallback error message
 * @returns {string} - Extracted error message
 */
export function getErrorMessage(error, fallback = 'حدث خطأ غير معروف') {
  // Handle different error response formats
  if (error.response) {
    // Server responded with error status
    const errorData = error.response.data;
    
    if (errorData) {
      // Try different possible message fields
      return errorData.message || 
             errorData.error || 
             errorData.msg || 
             errorData.errors?.[0] || 
             (typeof errorData === 'string' ? errorData : null) ||
             fallback;
    }
  } else if (error.request) {
    // Request was made but no response received
    return 'فشل في الاتصال بالخادم';
  } else if (error.message) {
    // Something else happened
    return error.message;
  }
  
  return fallback;
}

/**
 * Show success alert with backend message
 * @param {Object} store - Vuex store instance
 * @param {Object} response - API response
 * @param {string} fallback - Fallback success message
 * @param {Object} options - Additional alert options
 */
export function showSuccessAlert(store, response, fallback = 'تم تنفيذ العملية بنجاح', options = {}) {
  const message = getResponseMessage(response, fallback);
  
  store.dispatch('alerts/showAlert', {
    id: Date.now(),
    type: 'success',
    color: 'success',
    icon: 'mdi-check-circle',
    timeout: 3000,
    closable: true,
    message,
    ...options
  });
}

/**
 * Show error alert with backend message
 * @param {Object} store - Vuex store instance
 * @param {Object} error - Error object
 * @param {string} fallback - Fallback error message
 * @param {Object} options - Additional alert options
 */
export function showErrorAlert(store, error, fallback = 'حدث خطأ غير معروف', options = {}) {
  const message = getErrorMessage(error, fallback);
  
  store.dispatch('alerts/showAlert', {
    id: Date.now(),
    type: 'error',
    color: 'error',
    icon: 'mdi-alert-circle',
    timeout: 3000,
    closable: true,
    message,
    ...options
  });
}

/**
 * Show warning alert
 * @param {Object} store - Vuex store instance
 * @param {string} message - Warning message
 * @param {Object} options - Additional alert options
 */
export function showWarningAlert(store, message, options = {}) {
  store.dispatch('alerts/showAlert', {
    id: Date.now(),
    type: 'warning',
    color: 'warning',
    icon: 'mdi-alert',
    timeout: 3000,
    closable: true,
    message,
    ...options
  });
}

/**
 * Show info alert
 * @param {Object} store - Vuex store instance
 * @param {string} message - Info message
 * @param {Object} options - Additional alert options
 */
export function showInfoAlert(store, message, options = {}) {
  store.dispatch('alerts/showAlert', {
    id: Date.now(),
    type: 'info',
    color: 'info',
    icon: 'mdi-information',
    timeout: 3000,
    closable: true,
    message,
    ...options
  });
} 